import { ArrowLeft, Info, MapPin, Plus, Truck, User, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { typography } from '../../styles/typography';
import { driverApi } from '../Driver/driverApi';
import { getVehicleById } from '../Onboarding/vehicleOnboardingApi';
import { getAllSites, getSiteDetails } from '../SiteManagement/siteApi';
import { Card, PrimaryButton } from '../ui';
import EntitySelectionDialog from './EntitySelectionDialog';

const createFieldErrorState = () => ({
  vehicle: false,
  driver: false,
  docking_station: false,
  material: false,
});

const AssignConvo = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rideIdParam = searchParams.get('rideId');
  const isEditMode = !!rideIdParam;
  
  // State for trucks
  const [trucks, setTrucks] = useState([]);
  
  // State for available resources
  const [availableVehicles, setAvailableVehicles] = useState([]);
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [availableDockingStations, setAvailableDockingStations] = useState([]);
  
  // State for counts
  const [totalTrucksAvailable, setTotalTrucksAvailable] = useState(-1); // -1 means loading
  const [totalTrucksAssigned, setTotalTrucksAssigned] = useState(1);
  
  // State for preview mode
  const [isPreview, setIsPreview] = useState(false);
  
  // State for ride data (from URL params or localStorage)
  const [rideData, setRideData] = useState(null);
  
  // State for dialogs
  const [showVehicleDialog, setShowVehicleDialog] = useState(false);
  const [showDriverDialog, setShowDriverDialog] = useState(false);
  const [showDockingStationDialog, setShowDockingStationDialog] = useState(false);
  const [currentTruckIndex, setCurrentTruckIndex] = useState(null);
  const [showRideInfoDialog, setShowRideInfoDialog] = useState(false);
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [validationMessage, setValidationMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState([]);

  // State for original ride data (in edit mode)
  const [originalRide, setOriginalRide] = useState(null);

  // Load ride data from URL params or localStorage
  useEffect(() => {
    const rideDataParam = searchParams.get('rideData');
    if (rideDataParam) {
      try {
        const decoded = JSON.parse(decodeURIComponent(rideDataParam));
        setRideData(decoded);
        
        // In edit mode, extract original ride data if available
        if (decoded.originalRide) {
          setOriginalRide(decoded.originalRide);
        }
      } catch (e) {
        console.error('Error parsing ride data:', e);
        // Try to get from localStorage as fallback
        const stored = localStorage.getItem('pendingRideData');
        if (stored) {
          try {
            const parsed = JSON.parse(stored);
            setRideData(parsed);
            if (parsed.originalRide) {
              setOriginalRide(parsed.originalRide);
            }
          } catch (e2) {
            console.error('Error parsing stored ride data:', e2);
            setError('Failed to load ride data. Please go back and create the ride again.');
          }
        }
      }
    } else {
      // Try to get from localStorage
      const stored = localStorage.getItem('pendingRideData');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setRideData(parsed);
          if (parsed.originalRide) {
            setOriginalRide(parsed.originalRide);
          }
        } catch (e) {
          console.error('Error parsing stored ride data:', e);
          setError('Failed to load ride data. Please go back and create the ride again.');
        }
      } else {
        setError('No ride data found. Please go back and create the ride again.');
      }
    }
  }, [searchParams]);

  // Fetch initial data and add first truck or load existing convoy
  useEffect(() => {
    if (rideData) {
      fetchInitialData();
      if (isEditMode && rideIdParam) {
        // Wait a bit for originalRide to be set if it's being loaded
        const timer = setTimeout(() => {
          loadExistingConvoy();
        }, 100);
        return () => clearTimeout(timer);
      } else {
        addFirstTruck();
      }
    }
  }, [rideData, isEditMode, rideIdParam, originalRide]);

  // Fetch available vehicles, drivers, and docking stations
  const fetchInitialData = async () => {
    if (!rideData) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const fromSiteId = rideData.from?.site_id;
      const toSiteId = rideData.to?.site_id;
      const fromDate = rideData.from_date;
      const fromTime = rideData.from_time;
      const toDate = rideData.to_date;
      const toTime = rideData.to_time;

      // Fetch all sites for docking stations
      const sitesData = await getAllSites();
      const sortedSites = [...sitesData].sort((a, b) => 
        (a.site_name || '').localeCompare(b.site_name || '')
      );
      setAvailableDockingStations(sortedSites);

      // Fetch available vehicles using the API with date/time filtering
      // In edit mode, we still fetch available vehicles but don't restrict to only available ones
      const vehiclesData = await driverApi.fetchAvailableVehicles({
        fromSiteId,
        toSiteId,
        fromDate,
        fromTime,
        toDate,
        toTime,
      });
      
      const uniqueVehicles = [];
      const vehicleIds = new Set();
      
      vehiclesData.forEach(vehicle => {
        const id = vehicle.vehicle_id?.toString() || vehicle.id?.toString();
        if (id && !vehicleIds.has(id)) {
          vehicleIds.add(id);
          uniqueVehicles.push({
            vehicle_id: id,
            registration_no: vehicle.registration_no || vehicle.model_name || 'Unknown',
            model_name: vehicle.model_name || 'Unknown',
            vehicle_type: vehicle.vehicle_type || 'Truck',
          });
        }
      });
      
      // In edit mode, also include vehicles that are already assigned to this ride
      if (isEditMode && rideIdParam && trucks.length > 0) {
        trucks.forEach(truck => {
          if (truck.vehicle.id && !vehicleIds.has(truck.vehicle.id)) {
            vehicleIds.add(truck.vehicle.id);
            uniqueVehicles.push({
              vehicle_id: truck.vehicle.id,
              registration_no: truck.vehicle.name || 'Unknown',
              model_name: truck.vehicle.name || 'Unknown',
              vehicle_type: 'Truck',
            });
          }
        });
      }
      
      uniqueVehicles.sort((a, b) => 
        (a.model_name || '').localeCompare(b.model_name || '')
      );
      setAvailableVehicles(uniqueVehicles);
      setTotalTrucksAvailable(uniqueVehicles.length);

      // Fetch available drivers using the API with date/time filtering
      const driversData = await driverApi.fetchAvailableDrivers({
        fromDate,
        fromTime,
        toDate,
        toTime,
      });
      
      const uniqueDrivers = [];
      const driverIds = new Set();
      
      driversData.forEach(driver => {
        const id = driver.user_id?.toString() || driver.id?.toString();
        if (id && !driverIds.has(id)) {
          driverIds.add(id);
          uniqueDrivers.push({
            user_id: id,
            user_name: driver.user_name || driver.name || 'Unknown',
            spec_role: driver.spec_role || 'Driver',
          });
        }
      });
      
      // In edit mode, also include drivers that are already assigned to this ride
      if (isEditMode && rideIdParam && trucks.length > 0) {
        trucks.forEach(truck => {
          if (truck.driver.id && !driverIds.has(truck.driver.id)) {
            driverIds.add(truck.driver.id);
            uniqueDrivers.push({
              user_id: truck.driver.id,
              user_name: truck.driver.name || 'Unknown',
              spec_role: 'Driver',
            });
          }
        });
      }
      
      uniqueDrivers.sort((a, b) => 
        (a.user_name || '').localeCompare(b.user_name || '')
      );
      setAvailableDrivers(uniqueDrivers);

    } catch (err) {
      console.error('Error fetching initial data:', err);
      setError('Failed to load vehicles, drivers, or docking stations. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Update available vehicles and drivers to include already-assigned ones (for edit mode)
  useEffect(() => {
    if (isEditMode && trucks.length > 0) {
      // Add already-assigned vehicles to available vehicles if not already present
      // Also update vehicle names if they've been fetched
      setAvailableVehicles(prevVehicles => {
        const vehicleIds = new Set(prevVehicles.map(v => (v.vehicle_id || v.id)?.toString()));
        const newVehicles = [...prevVehicles];
        let hasChanges = false;
        
        trucks.forEach(truck => {
          if (truck.vehicle.id) {
            const vehicleId = truck.vehicle.id.toString();
            const existingVehicle = newVehicles.find(v => (v.vehicle_id || v.id)?.toString() === vehicleId);
            
            if (!existingVehicle) {
              // Vehicle not in list, add it (only if we have a name)
              if (truck.vehicle.name && truck.vehicle.name !== 'Unknown') {
                vehicleIds.add(vehicleId);
                newVehicles.push({
                  vehicle_id: vehicleId,
                  registration_no: truck.vehicle.name,
                  model_name: truck.vehicle.name,
                  vehicle_type: 'Truck',
                });
                hasChanges = true;
              }
            } else if (truck.vehicle.name && truck.vehicle.name !== 'Unknown' && 
                       existingVehicle.model_name !== truck.vehicle.name) {
              // Vehicle exists but name has been updated, update it
              existingVehicle.model_name = truck.vehicle.name;
              existingVehicle.registration_no = truck.vehicle.name;
              hasChanges = true;
            }
          }
        });
        
        if (hasChanges) {
          newVehicles.sort((a, b) => 
            (a.model_name || '').localeCompare(b.model_name || '')
          );
          setTotalTrucksAvailable(newVehicles.length);
          return newVehicles;
        }
        return prevVehicles;
      });
    }
  }, [isEditMode, trucks]);

  // Update available drivers to include already-assigned ones (for edit mode)
  useEffect(() => {
    if (isEditMode && trucks.length > 0 && availableDrivers.length > 0) {
      // Add already-assigned drivers to available drivers if not already present
      const driverIds = new Set(availableDrivers.map(d => (d.user_id || d.id)?.toString()));
      const newDrivers = [...availableDrivers];
      
      trucks.forEach(truck => {
        if (truck.driver.id) {
          const driverId = truck.driver.id.toString();
          if (!driverIds.has(driverId)) {
            driverIds.add(driverId);
            newDrivers.push({
              user_id: driverId,
              user_name: truck.driver.name || 'Unknown',
              spec_role: 'Driver',
            });
          }
        }
      });
      
      if (newDrivers.length !== availableDrivers.length) {
        newDrivers.sort((a, b) => 
          (a.user_name || '').localeCompare(b.user_name || '')
        );
        setAvailableDrivers(newDrivers);
      }
    }
  }, [isEditMode, trucks]);

  // Update truck names after available data is loaded (for edit mode)
  useEffect(() => {
    if (isEditMode && trucks.length > 0 && availableVehicles.length > 0 && availableDrivers.length > 0 && availableDockingStations.length > 0) {
      setTrucks(prevTrucks => prevTrucks.map(truck => {
        const vehicle = availableVehicles.find(v => (v.vehicle_id || v.id) === truck.vehicle.id);
        const driver = availableDrivers.find(d => (d.user_id || d.id) === truck.driver.id);
        const dockingStation = availableDockingStations.find(s => (s.site_id || s.id) === truck.docking_station.id);
        
        return {
          ...truck,
          vehicle: {
            ...truck.vehicle,
            name: vehicle ? (vehicle.model_name || vehicle.registration_no || truck.vehicle.name) : truck.vehicle.name
          },
          driver: {
            ...truck.driver,
            name: driver ? (driver.user_name || driver.name || truck.driver.name) : truck.driver.name
          },
          docking_station: {
            ...truck.docking_station,
            name: dockingStation ? (dockingStation.site_name || truck.docking_station.name) : truck.docking_station.name
          }
        };
      }));
    }
  }, [isEditMode, availableVehicles, availableDrivers, availableDockingStations]);

  // Add first truck unconditionally
  const addFirstTruck = () => {
    setTrucks([{
      vehicle: { id: null, name: null },
      driver: { id: null, name: null },
      docking_station: { id: null, name: null },
      material: null,
    }]);
    setTotalTrucksAssigned(1);
    setFieldErrors([createFieldErrorState()]);
  };

  // Load existing convoy details for edit mode
  const loadExistingConvoy = async () => {
    if (!rideIdParam) return;
    
    try {
      // First, try to use originalRide data if available (passed from Create_Ride_Schedule)
      let ride = originalRide;
      
      // If not available, fetch from API
      if (!ride || !ride.convoy_details) {
        const schedules = await driverApi.fetchFutureDriverSchedules();
        ride = schedules.find(r => r.ride_id === rideIdParam);
      }
      
      if (ride && ride.convoy_details && Array.isArray(ride.convoy_details) && ride.convoy_details.length > 0) {
        // Map convoy details to truck format
        const loadedTrucks = ride.convoy_details.map(convoy => {
          // Handle different convoy detail formats
          const vehicleId = convoy.vehicle_id || (convoy.vehicle?.id || convoy.vehicle);
          const driverId = convoy.user_id || (convoy.driver?.id || convoy.driver);
          const dockingStationId = convoy.docking_station || (convoy.docking_station?.id || convoy.docking_station);
          
          // Try to get names from the convoy object if available
          const vehicleName = convoy.vehicle?.name || convoy.vehicle_name || null;
          const driverName = convoy.driver?.name || convoy.driver_name || convoy.user_name || null;
          const dockingStationName = convoy.docking_station?.name || convoy.docking_station_name || null;
          
          return {
            vehicle: { 
              id: vehicleId || null, 
              name: vehicleName // Will be updated when vehicles are loaded if null
            },
            driver: { 
              id: driverId || null, 
              name: driverName // Will be fetched if null
            },
            docking_station: { 
              id: dockingStationId || null, 
              name: dockingStationName // Will be fetched if null
            },
            material: convoy.material || null,
          };
        });
        
        // Fetch vehicle names, driver names and site names for trucks that don't have them
        const trucksWithNames = await Promise.all(
          loadedTrucks.map(async (truck) => {
            const updatedTruck = { ...truck };
            
            // Fetch vehicle name if we have vehicle ID but no name
            if (truck.vehicle.id && !truck.vehicle.name) {
              try {
                const vehicleDetails = await getVehicleById(truck.vehicle.id);
                updatedTruck.vehicle.name = vehicleDetails?.model_name || vehicleDetails?.registration_no || vehicleDetails?.vehicle_name || truck.vehicle.id;
              } catch (error) {
                console.error(`Error fetching vehicle details for ${truck.vehicle.id}:`, error);
                updatedTruck.vehicle.name = truck.vehicle.id; // Fallback to ID
              }
            }
            
            // Fetch driver name if we have driver ID but no name
            if (truck.driver.id && !truck.driver.name) {
              try {
                const driverDetails = await driverApi.fetchDriverDetails(truck.driver.id);
                updatedTruck.driver.name = driverDetails?.user_name || driverDetails?.name || truck.driver.id;
              } catch (error) {
                console.error(`Error fetching driver details for ${truck.driver.id}:`, error);
                updatedTruck.driver.name = truck.driver.id; // Fallback to ID
              }
            }
            
            // Fetch site name if we have docking station ID but no name
            if (truck.docking_station.id && !truck.docking_station.name) {
              try {
                const siteDetails = await getSiteDetails(truck.docking_station.id);
                updatedTruck.docking_station.name = siteDetails?.site_name || truck.docking_station.id;
              } catch (error) {
                console.error(`Error fetching site details for ${truck.docking_station.id}:`, error);
                updatedTruck.docking_station.name = truck.docking_station.id; // Fallback to ID
              }
            }
            
            return updatedTruck;
          })
        );
        
        setTrucks(trucksWithNames);
        setTotalTrucksAssigned(trucksWithNames.length);
        setFieldErrors(trucksWithNames.map(() => createFieldErrorState()));
      } else {
        // No convoy details found, add first truck
        addFirstTruck();
      }
    } catch (error) {
      console.error('Error loading existing convoy:', error);
      // Fallback to adding first truck
      addFirstTruck();
    }
  };

  // Add a new truck
  const addTruck = () => {
    // In edit mode, allow adding trucks even if limit is reached (limit might not be accurate)
    // In create mode, respect the limit
    if (isEditMode || trucks.length < totalTrucksAvailable || totalTrucksAvailable <= 0) {
      setTrucks(prev => [...prev, {
        vehicle: { id: null, name: null },
        driver: { id: null, name: null },
        docking_station: { id: null, name: null },
        material: null,
      }]);
      setTotalTrucksAssigned(prev => prev + 1);
      setFieldErrors(prev => [...prev, createFieldErrorState()]);
    } else {
      alert('Cannot add more trucks. Maximum limit reached!');
    }
  };

  // Remove a truck
  const removeTruck = (index) => {
    if (trucks.length <= 1) {
      alert('At least one truck is required');
      return;
    }
    
    setTrucks(prev => prev.filter((_, i) => i !== index));
    setTotalTrucksAssigned(prev => prev - 1);
    setFieldErrors(prev => prev.filter((_, i) => i !== index));
  };

  // Validate truck details
  const validateTruckDetails = () => {
    const errors = trucks.map(truck => ({
      vehicle: !truck.vehicle.id,
      driver: !truck.driver.id,
      docking_station: !truck.docking_station.id,
      material: !truck.material || truck.material.trim() === '',
    }));

    const isValid = errors.every(errorSet =>
      !errorSet.vehicle && !errorSet.driver && !errorSet.docking_station && !errorSet.material
    );

    setFieldErrors(isValid ? trucks.map(() => createFieldErrorState()) : errors);
    return isValid;
  };

  const clearFieldError = (index, field) => {
    setFieldErrors(prev => {
      const next = [...prev];
      while (next.length <= index) {
        next.push(createFieldErrorState());
      }
      next[index] = {
        ...next[index],
        [field]: false,
      };
      return next;
    });
    setValidationMessage('');
  };

  // Handle vehicle selection
  const handleVehicleSelection = (index) => {
    setCurrentTruckIndex(index);
    setShowVehicleDialog(true);
  };

  const handleVehicleConfirm = (vehicleId) => {
    if (!vehicleId || currentTruckIndex === null) return;
    
    const selectedVehicle = availableVehicles.find(v => 
      v.vehicle_id === vehicleId || v.id === vehicleId
    );
    
    if (!selectedVehicle) return;
    
    setTrucks(prev => {
      const updated = [...prev];
      updated[currentTruckIndex] = {
        ...updated[currentTruckIndex],
        vehicle: {
          id: selectedVehicle.vehicle_id || selectedVehicle.id,
          name: selectedVehicle.model_name || selectedVehicle.registration_no,
        },
      };
      return updated;
    });

    clearFieldError(currentTruckIndex, 'vehicle');
    
    setShowVehicleDialog(false);
    setCurrentTruckIndex(null);
  };

  // Handle driver selection
  const handleDriverSelection = (index) => {
    setCurrentTruckIndex(index);
    setShowDriverDialog(true);
  };

  const handleDriverConfirm = (driverId) => {
    if (!driverId || currentTruckIndex === null) return;
    
    const selectedDriver = availableDrivers.find(d => 
      d.user_id === driverId || d.id === driverId
    );
    
    if (!selectedDriver) return;
    
    setTrucks(prev => {
      const updated = [...prev];
      updated[currentTruckIndex] = {
        ...updated[currentTruckIndex],
        driver: {
          id: selectedDriver.user_id || selectedDriver.id,
          name: selectedDriver.user_name || selectedDriver.name,
        },
      };
      return updated;
    });

    clearFieldError(currentTruckIndex, 'driver');
    
    setShowDriverDialog(false);
    setCurrentTruckIndex(null);
  };

  // Handle docking station selection
  const handleDockingStationSelection = (index) => {
    setCurrentTruckIndex(index);
    setShowDockingStationDialog(true);
  };

  const handleDockingStationConfirm = (siteId) => {
    if (!siteId || currentTruckIndex === null) return;
    
    const selectedStation = availableDockingStations.find(s => 
      s.site_id === siteId || s.id === siteId
    );
    
    if (!selectedStation) return;
    
    setTrucks(prev => {
      const updated = [...prev];
      updated[currentTruckIndex] = {
        ...updated[currentTruckIndex],
        docking_station: {
          id: selectedStation.site_id || selectedStation.id,
          name: selectedStation.site_name,
        },
      };
      return updated;
    });

    clearFieldError(currentTruckIndex, 'docking_station');
    
    setShowDockingStationDialog(false);
    setCurrentTruckIndex(null);
  };

  // Get available vehicles for selection (filter out already assigned)
  const getAvailableVehiclesForSelection = () => {
    if (currentTruckIndex === null) return [];
    
    const currentTruck = trucks[currentTruckIndex];
    return availableVehicles.filter(vehicle => {
      const vehicleId = vehicle.vehicle_id || vehicle.id;
      // Include if not assigned to any other truck (except current truck)
      return !trucks.some((t, i) => 
        i !== currentTruckIndex && t.vehicle.id === vehicleId
      );
    });
  };

  // Get available drivers for selection (filter out already assigned)
  const getAvailableDriversForSelection = () => {
    if (currentTruckIndex === null) return [];
    
    const currentTruck = trucks[currentTruckIndex];
    return availableDrivers.filter(driver => {
      const driverId = driver.user_id || driver.id;
      // Include if not assigned to any other truck (except current truck)
      return !trucks.some((t, i) => 
        i !== currentTruckIndex && t.driver.id === driverId
      );
    });
  };

  // Handle material input change
  const handleMaterialChange = (index, value) => {
    setTrucks(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        material: value,
      };
      return updated;
    });

    if (value && value.trim()) {
      clearFieldError(index, 'material');
    }
  };

  // Handle preview toggle
  const handlePreviewToggle = () => {
    if (isPreview) {
      setIsPreview(false);
      setValidationMessage('');
      return;
    }

    if (!validateTruckDetails()) {
      setValidationMessage('Please fill all the details before previewing');
      return;
    }

    setValidationMessage('');
    setIsPreview(true);
  };

  // Convert date from DD-MM-YYYY to YYYY-MM-DD format (for API)
  const convertDateToAPIFormat = (dateString) => {
    if (!dateString) return '';
    try {
      // Check if it's already in YYYY-MM-DD format
      const parts = dateString.split('-');
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          // Already in YYYY-MM-DD format
          return dateString;
        } else if (parts[2].length === 4) {
          // DD-MM-YYYY format, convert to YYYY-MM-DD
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }
      return dateString;
    } catch (e) {
      console.error('Error converting date format:', e);
      return dateString;
    }
  };

  // Convert time from 12-hour format (hh:mm AM/PM) to 24-hour format (HH:mm)
  const convertTimeTo24Hour = (timeString) => {
    if (!timeString) return '';
    try {
      // Check if it's already in 24-hour format (HH:mm)
      if (timeString.includes('AM') || timeString.includes('PM')) {
        // 12-hour format, convert to 24-hour
        const timeParts = timeString.trim().split(' ');
        if (timeParts.length === 2) {
          const [time, period] = timeParts;
          const [hours, minutes] = time.split(':').map(Number);
          
          let hour24 = hours;
          if (period.toUpperCase() === 'PM' && hours !== 12) {
            hour24 += 12;
          } else if (period.toUpperCase() === 'AM' && hours === 12) {
            hour24 = 0;
          }
          
          return `${String(hour24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
        }
      } else {
        // Already in 24-hour format or invalid, return as is
        return timeString;
      }
      return timeString;
    } catch (e) {
      console.error('Error converting time format:', e);
      return timeString;
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateTruckDetails()) {
      setValidationMessage('Please fill all the details before submitting');
      return;
    }

    setValidationMessage('');

    // Debug: Log trucks array before processing
    console.log('Trucks array before processing:', trucks);

    try {
      setIsSubmitting(true);
      setError(null);
      
      if (isEditMode && rideIdParam) {
        // Update existing schedule
        // Use same format as create: assigned_trucks, from_date/from_time/to_date/to_time (DD-MM-YYYY and 12-hour format)
        // For edit mode, always include all fields in assigned_trucks (even if unchanged)
        // Since validateTruckDetails() already ensures all fields are present, we can safely access them
        const assignedTrucks = trucks.map((truck, index) => {
          // Validation ensures all fields exist (truck.vehicle.id, truck.driver.id, etc.)
          // Since validation passed, we can safely access them directly
          // Build assigned truck object matching API format (same as create)
          return {
            vehicle: truck.vehicle.id,
            driver: truck.driver.id,
            docking_station: truck.docking_station.id,
            material: truck.material,
          };
        });
        
        const updateData = {
          ride_id: rideIdParam,
          from_site_id: rideData.from?.site_id,
          to_site_id: rideData.to?.site_id,
          from_date: rideData.from_date,
          from_time: rideData.from_time,
          to_date: rideData.to_date,
          to_time: rideData.to_time,
          average_speed: rideData.average_speed,
          from_supervisor_id: rideData.from_supervisor?.user_id,
          to_supervisor_id: rideData.to_supervisor?.user_id,
          assigned_trucks: assignedTrucks,
        };

        // Include halts if they exist in rideData
        if (rideData.halts && Array.isArray(rideData.halts) && rideData.halts.length > 0) {
          updateData.halts = rideData.halts;
        }

        console.log('Convoy Update Data:', updateData);
        
        const message = await driverApi.updateDriverSchedule(updateData);
        
        // Clear localStorage (both pending ride data and form data)
        localStorage.removeItem('pendingRideData');
        localStorage.removeItem('createRideFormData');
        
        // Set flag to refresh ride schedules when returning to list view
        sessionStorage.setItem('shouldRefreshRideSchedules', 'true');
        
        // Show success message and navigate back
        alert(message || 'Ride schedule updated successfully!');
        setSearchParams({});
      } else {
        // Create new schedule
        const assignedTrucks = trucks.map(truck => ({
          vehicle: truck.vehicle.id,
          driver: truck.driver.id,
          docking_station: truck.docking_station.id,
          material: truck.material,
        }));
        
        const finalData = {
          from_site_id: rideData.from?.site_id,
          to_site_id: rideData.to?.site_id,
          from_date: rideData.from_date,
          from_time: rideData.from_time,
          to_date: rideData.to_date,
          to_time: rideData.to_time,
          average_speed: rideData.average_speed,
          halts: rideData.halts || [],
          from_supervisor_id: rideData.from_supervisor?.user_id,
          to_supervisor_id: rideData.to_supervisor?.user_id,
          assigned_trucks: assignedTrucks,
        };

        console.log('Convoy Assignment Data:', finalData);
        
        const message = await driverApi.createDriverSchedule(finalData);
        
        // Clear localStorage (both pending ride data and form data)
        localStorage.removeItem('pendingRideData');
        localStorage.removeItem('createRideFormData');
        
        // Set flag to refresh ride schedules when returning to list view
        sessionStorage.setItem('shouldRefreshRideSchedules', 'true');
        
        // Show success message and navigate back
        alert(message || 'Ride schedule created successfully!');
        setSearchParams({});
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} driver schedule:`, error);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} schedule: ${error.message}`);
      setError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || !rideData) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full flex flex-col">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => setSearchParams({ view: 'create-ride' })}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            title="Back"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div>
            <h2 className={typography.h2} style={{ color: '#000000' }}>
              Convoy Assignment
            </h2>
          </div>
        </div>
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      {/* Header Section */}
      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={() => setSearchParams({ view: 'create-ride' })}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          title="Back"
        >
          <ArrowLeft className="w-6 h-6 text-gray-600" />
        </button>
        <div className="flex-1">
          <h2 className={typography.h2} style={{ color: '#000000' }}>
            {isEditMode ? 'Edit Convoy' : 'Convoy Assignment'}
          </h2>
          <p className={typography.p} style={{ color: '#666666', marginTop: '0.5rem' }}>
            {isEditMode 
              ? 'Update vehicles, drivers, and docking stations for the ride'
              : 'Assign vehicles, drivers, and docking stations for the ride'}
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <Card.Gradient className="mb-6 p-6 bg-green-600">
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <p className={typography.h4} style={{ color: '#000000', marginBottom: '0.75rem' }}>
              {totalTrucksAvailable === -1 
                ? 'Total No. of Vehicles Available: Loading...'
                : `Total No. of Vehicles Available: ${totalTrucksAvailable}`}
            </p>
            <p className={typography.h4} style={{ color: '#000000' }}>
              Total No. of Vehicles Assigned: {totalTrucksAssigned}
            </p>
          </div>
          <button
            onClick={() => setShowRideInfoDialog(true)}
            className="p-2 rounded-lg hover:bg-green-700 transition-colors"
            title="View Ride Information"
          >
            <Info className="w-6 h-6 text-white" />
          </button>
        </div>
        
        <div className="flex gap-3 justify-center">
          <PrimaryButton
            onClick={handlePreviewToggle}
            className="flex-1"
          >
            {isPreview ? 'Edit' : 'Check Preview'}
          </PrimaryButton>
          {isPreview && (
            <PrimaryButton
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </PrimaryButton>
          )}
        </div>
        {validationMessage && (
          <div className="mt-4 p-3 rounded border border-red-400 bg-red-50 text-red-700 text-sm text-center">
            {validationMessage}
          </div>
        )}
      </Card.Gradient>

      {/* Trucks List */}
      <div className="flex-1 overflow-y-auto mb-6">
        {isEditMode ? (
          // In edit mode, show assigned trucks (even if no available trucks)
          trucks.length === 0 ? (
            <div className="text-center py-8">
              <p className={typography.p} style={{ color: '#000000' }}>
                No trucks assigned to this ride. Please assign at least one truck.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {trucks.map((truck, index) => {
                const truckErrors = fieldErrors[index] || {};
                return (
                  <Card.Gradient key={index} className="p-6 border-2 border-green-400">
                  <div className="flex justify-between items-start mb-4">
                    <h5 className={typography.h5} style={{ color: '#000000' }}>
                      Assigned Vehicle • {index + 1}
                    </h5>
                    {trucks.length > 1 && (
                      <button
                        onClick={() => removeTruck(index)}
                        disabled={isPreview}
                        className={`p-1 rounded transition-colors ${
                          isPreview 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <X className={`w-5 h-5 ${isPreview ? 'text-gray-400' : 'text-gray-600'}`} />
                      </button>
                    )}
                  </div>

                  {/* Vehicle Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vehicle
                    </label>
                    <button
                      onClick={() => handleVehicleSelection(index)}
                      disabled={isPreview}
                      className={`w-full h-12 border rounded px-3 bg-white flex items-center gap-3 transition-colors ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : truckErrors.vehicle
                          ? 'border-red-500 hover:bg-gray-50'
                          : truck.vehicle.id
                          ? 'border-green-500 hover:bg-gray-50'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <Truck className={`w-5 h-5 ${
                        truck.vehicle.id
                          ? 'text-green-600'
                          : truckErrors.vehicle
                          ? 'text-red-500'
                          : 'text-gray-400'
                      }`} />
                      <span className={`flex-1 text-left ${
                        truck.vehicle.id
                          ? 'text-black'
                          : truckErrors.vehicle
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`}>
                        {truck.vehicle.name || truck.vehicle.id || 'Select Vehicle'}
                      </span>
                    </button>
                  </div>

                  {/* Driver Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Driver
                    </label>
                    <button
                      onClick={() => handleDriverSelection(index)}
                      disabled={isPreview}
                      className={`w-full h-12 border rounded px-3 bg-white flex items-center gap-3 transition-colors ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : truckErrors.driver
                          ? 'border-red-500 hover:bg-gray-50'
                          : truck.driver.id
                          ? 'border-green-500 hover:bg-gray-50'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <User className={`w-5 h-5 ${
                        truck.driver.id
                          ? 'text-green-600'
                          : truckErrors.driver
                          ? 'text-red-500'
                          : 'text-gray-400'
                      }`} />
                      <span className={`flex-1 text-left ${
                        truck.driver.id
                          ? 'text-black'
                          : truckErrors.driver
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`}>
                        {truck.driver.name || truck.driver.id || 'Select Driver'}
                      </span>
                    </button>
                  </div>

                  {/* Docking Station Selection */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Docking Station
                    </label>
                    <button
                      onClick={() => handleDockingStationSelection(index)}
                      disabled={isPreview}
                      className={`w-full h-12 border rounded px-3 bg-white flex items-center gap-3 transition-colors ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : truckErrors.docking_station
                          ? 'border-red-500 hover:bg-gray-50'
                          : truck.docking_station.id
                          ? 'border-green-500 hover:bg-gray-50'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <MapPin className={`w-5 h-5 ${
                        truck.docking_station.id
                          ? 'text-green-600'
                          : truckErrors.docking_station
                          ? 'text-red-500'
                          : 'text-gray-400'
                      }`} />
                      <span className={`flex-1 text-left ${
                        truck.docking_station.id
                          ? 'text-black'
                          : truckErrors.docking_station
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`}>
                        {truck.docking_station.name || truck.docking_station.id || 'Select Docking Station'}
                      </span>
                    </button>
                  </div>

                  {/* Material Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Material transferring/Ticked_id
                    </label>
                    <input
                      type="text"
                      value={truck.material || ''}
                      onChange={(e) => handleMaterialChange(index, e.target.value)}
                      disabled={isPreview}
                      placeholder="Enter material or ticket ID"
                      className={`w-full h-12 px-3 border rounded bg-white focus:outline-none focus:ring-2 ${
                        isPreview
                          ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                          : truckErrors.material
                          ? 'border-red-500 focus:ring-red-500'
                          : truck.material
                          ? 'border-green-500 focus:ring-green-500'
                          : 'border-gray-300 focus:ring-green-500'
                      }`}
                    />
                  </div>
                  </Card.Gradient>
                );
              })}
            </div>
          )
        ) : totalTrucksAvailable === 0 ? (
          <div className="text-center py-8">
            <p className={typography.p} style={{ color: '#000000' }}>
              No trucks available, please Onboard vehicles for the site {rideData?.from?.site_name ? `(${rideData.from.site_name})` : ''}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {trucks.map((truck, index) => {
              const truckErrors = fieldErrors[index] || {};
              return (
                <Card.Gradient key={index} className="p-6 border-2 border-green-400">
                <div className="flex justify-between items-start mb-4">
                  <h5 className={typography.h5} style={{ color: '#000000' }}>
                    Truck • {index + 1}
                  </h5>
                  {trucks.length > 1 && (
                    <button
                      onClick={() => removeTruck(index)}
                      disabled={isPreview}
                      className={`p-1 rounded transition-colors ${
                        isPreview 
                          ? 'opacity-50 cursor-not-allowed' 
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <X className={`w-5 h-5 ${isPreview ? 'text-gray-400' : 'text-gray-600'}`} />
                    </button>
                  )}
                </div>

                {/* Vehicle Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle
                  </label>
                  <button
                    onClick={() => handleVehicleSelection(index)}
                    disabled={isPreview}
                    className={`w-full h-12 border rounded px-3 bg-white flex items-center gap-3 transition-colors ${
                      isPreview
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                        : truckErrors.vehicle
                        ? 'border-red-500 hover:bg-gray-50'
                        : truck.vehicle.id
                        ? 'border-green-500 hover:bg-gray-50'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Truck className={`w-5 h-5 ${
                      truck.vehicle.id
                        ? 'text-green-600'
                        : truckErrors.vehicle
                        ? 'text-red-500'
                        : 'text-gray-400'
                    }`} />
                    <span className={`flex-1 text-left ${
                      truck.vehicle.id
                        ? 'text-black'
                        : truckErrors.vehicle
                        ? 'text-red-600'
                        : 'text-gray-500'
                    }`}>
                      {truck.vehicle.name || 'Select Vehicle'}
                    </span>
                  </button>
                </div>

                {/* Driver Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Driver
                  </label>
                  <button
                    onClick={() => handleDriverSelection(index)}
                    disabled={isPreview}
                    className={`w-full h-12 border rounded px-3 bg-white flex items-center gap-3 transition-colors ${
                      isPreview
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                        : truckErrors.driver
                        ? 'border-red-500 hover:bg-gray-50'
                        : truck.driver.id
                        ? 'border-green-500 hover:bg-gray-50'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <User className={`w-5 h-5 ${
                      truck.driver.id
                        ? 'text-green-600'
                        : truckErrors.driver
                        ? 'text-red-500'
                        : 'text-gray-400'
                    }`} />
                    <span className={`flex-1 text-left ${
                      truck.driver.id
                        ? 'text-black'
                        : truckErrors.driver
                        ? 'text-red-600'
                        : 'text-gray-500'
                    }`}>
                      {truck.driver.name || 'Select Driver'}
                    </span>
                  </button>
                </div>

                {/* Docking Station Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Docking Station
                  </label>
                  <button
                    onClick={() => handleDockingStationSelection(index)}
                    disabled={isPreview}
                    className={`w-full h-12 border rounded px-3 bg-white flex items-center gap-3 transition-colors ${
                      isPreview
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                        : truckErrors.docking_station
                        ? 'border-red-500 hover:bg-gray-50'
                        : truck.docking_station.id
                        ? 'border-green-500 hover:bg-gray-50'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <MapPin className={`w-5 h-5 ${
                      truck.docking_station.id
                        ? 'text-green-600'
                        : truckErrors.docking_station
                        ? 'text-red-500'
                        : 'text-gray-400'
                    }`} />
                    <span className={`flex-1 text-left ${
                      truck.docking_station.id
                        ? 'text-black'
                        : truckErrors.docking_station
                        ? 'text-red-600'
                        : 'text-gray-500'
                    }`}>
                      {truck.docking_station.name || 'Select Docking Station'}
                    </span>
                  </button>
                </div>

                {/* Material Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Material transferring/Ticked_id
                  </label>
                  <input
                    type="text"
                    value={truck.material || ''}
                    onChange={(e) => handleMaterialChange(index, e.target.value)}
                    disabled={isPreview}
                    placeholder="Enter material or ticket ID"
                    className={`w-full h-12 px-3 border rounded bg-white focus:outline-none focus:ring-2 ${
                      isPreview
                        ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                        : truckErrors.material
                        ? 'border-red-500 focus:ring-red-500'
                        : truck.material
                        ? 'border-green-500 focus:ring-green-500'
                        : 'border-gray-300 focus:ring-green-500'
                    }`}
                  />
                </div>
                </Card.Gradient>
              );
            })}
          </div>
        )}

        {/* Add Truck Button */}
        {(totalTrucksAvailable > 0 || (isEditMode && trucks.length > 0)) && (
          <button
            onClick={addTruck}
            disabled={isPreview || (totalTrucksAvailable > 0 && trucks.length >= totalTrucksAvailable)}
            className={`w-full h-12 border-2 rounded-lg flex items-center justify-center transition-colors mt-4 ${
              isPreview || (totalTrucksAvailable > 0 && trucks.length >= totalTrucksAvailable)
                ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                : 'border-green-400 bg-green-50 hover:bg-green-100'
            }`}
          >
            <Plus className={`w-6 h-6 ${isPreview || (totalTrucksAvailable > 0 && trucks.length >= totalTrucksAvailable) ? 'text-gray-400' : 'text-green-600'}`} />
          </button>
        )}
      </div>

      {/* Vehicle Selection Dialog */}
      {showVehicleDialog && createPortal(
        <EntitySelectionDialog
          isOpen={showVehicleDialog}
          onClose={() => {
            setShowVehicleDialog(false);
            setCurrentTruckIndex(null);
          }}
          onConfirm={handleVehicleConfirm}
          entityType="vehicle"
          dialogTitle="Select Vehicle"
          availableEntities={getAvailableVehiclesForSelection()}
        />,
        document.body
      )}

      {/* Driver Selection Dialog */}
      {showDriverDialog && createPortal(
        <EntitySelectionDialog
          isOpen={showDriverDialog}
          onClose={() => {
            setShowDriverDialog(false);
            setCurrentTruckIndex(null);
          }}
          onConfirm={handleDriverConfirm}
          entityType="employee"
          dialogTitle="Select Driver"
          availableEntities={getAvailableDriversForSelection()}
        />,
        document.body
      )}

      {/* Docking Station Selection Dialog */}
      {showDockingStationDialog && createPortal(
        <EntitySelectionDialog
          isOpen={showDockingStationDialog}
          onClose={() => {
            setShowDockingStationDialog(false);
            setCurrentTruckIndex(null);
          }}
          onConfirm={handleDockingStationConfirm}
          entityType="site"
          dialogTitle="Select Docking Station"
          availableEntities={availableDockingStations.map(site => ({
            site_id: site.site_id || site.id,
            site_name: site.site_name,
            address: site.address || site.site_name,
            site_type: 'Docking Station',
          }))}
        />,
        document.body
      )}

      {/* Ride Info Dialog */}
      {showRideInfoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className={typography.h3} style={{ color: '#000000' }}>
                  Ride Information
                </h3>
                <button
                  onClick={() => setShowRideInfoDialog(false)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <X className="w-6 h-6 text-gray-500" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div>
                  <span className="font-semibold text-gray-700">From: </span>
                  <span className="text-gray-900">{rideData.from?.site_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">To: </span>
                  <span className="text-gray-900">{rideData.to?.site_name || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">From Date: </span>
                  <span className="text-gray-900">{rideData.from_date || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">From Time: </span>
                  <span className="text-gray-900">{rideData.from_time || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">To Date: </span>
                  <span className="text-gray-900">{rideData.to_date || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">To Time: </span>
                  <span className="text-gray-900">{rideData.to_time || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">From Supervisor: </span>
                  <span className="text-gray-900">{rideData.from_supervisor?.user_name || rideData.from_supervisor?.name || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">To Supervisor: </span>
                  <span className="text-gray-900">{rideData.to_supervisor?.user_name || rideData.to_supervisor?.name || 'N/A'}</span>
                </div>
                <div>
                  <span className="font-semibold text-gray-700">Average Speed: </span>
                  <span className="text-gray-900">{rideData.average_speed || 'N/A'} km/h</span>
                </div>
                {rideData.halts && rideData.halts.length > 0 && (
                  <div>
                    <span className="font-semibold text-gray-700">Halts: </span>
                    <span className="text-gray-900">
                      {rideData.halts.map(h => h.address || 'Unknown').join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignConvo;

